import { mapGetters, mapMutations } from 'vuex';
import Forms from './../../utils/forms/forms';
import { sortBatchOptions, defaultBatchId } from './../../utils/batchOptions';
import productService from './../../services/product';
import batchService from './../../services/batch';
import stockService from './../../services/stock';
import store from './../../store';
import organizationSettingsService from './../../services/organizationSettings';
import bulkAddProductModal from './../../components/bulkAddProductModal/bulkAddProductModal.vue';

export default {
  data() {
    return {
      form: new Forms({
        productId: {
          value: '',
          type: 'autocomplete',
          attributes: {
            isSearch: true,
            searchMethod: this.searchMethod,
            loading: true,
          },
        },
        qty: {
          value: '0',
          type: 'text',
        },
      }),
      additionalButtons: [{
        text: this.$t('table.orderProducts.addProductsButton'),
        action: this.showBulkAddProductModal,
      }],
      hiddenFields: [],
    };
  },

  props: {
    fields: {
      type: Object,
      required: true,
    },
    translation: {
      type: String,
      required: false,
      default: 'table.productsCustom',
    },
    page: {
      type: String,
      required: true,
    },
    transferType: {
      type: String,
      required: false,
      default: '',
    },
    isImport: {
      type: Boolean,
      required: false,
      default: false,
    },
    isExport: {
      type: Boolean,
      required: false,
      default: false,
    },

    exportClickHandler: {
      type: Function,
      default: () => false,
    },
    server: {
      type: Object,
      default: () => ({}),
      required: false,
    },

    additionalData: {
      type: Object,
      default: () => ({}),
      required: false,
    },

    disableForm: {
      type: Boolean,
      default: () => false,
      required: false,
    },

  },

  computed: {
    ...mapGetters({
      products: 'allProducts',
      currentStockProducts: 'currentStockProductsCustom',
      currentSelectedWarehouse: 'currentSelectedWarehouse',
      account: 'account',
      settings: 'organizationSettings',
      batches: 'allBatches',
      transactionTypes: 'allTransactionTypes',
    }),

    transferTypeCode() {
      if (!this.transactionTypes) {
        return;
      }
      const type = this.transactionTypes.find(item => +item.id === +this.transferType);
      return type ? type.code : '';
    },

    isBatchTransferPage() {
      return this.page === 'transfer' && this.transferType && this.transferTypeCode === 'TB';
    },

    isTransferToBatchPage() {
      return this.page === 'transfer' && this.transferType && this.transferTypeCode === 'TTB';
    },

    hasProduction() {
      if (this.account.allowProducerBatches) return true;
      if (this.account.modules.length > 0) {
        return this.account.modules.find(m => m.name.toString() === 'Production');
      }
      return false;
    },

    list() {
      return [];
    },

    checkWarehouse() {
      if (this.disableForm) return true;
      return this.currentSelectedWarehouse <= 0;
    },
    showImport() {
      return this.isImport;
    },
  },

  watch: {

    /**
     * Watch product select for autocomplete other fields with defaults.
     */
    'form.productId': function (product) {
      const me = this;
      if (product.id != undefined) {
        this.setBatchOptions(product);
      }

      if (this.page == 'adjustment') {
        if (this.currentSelectedWarehouse > 0) {
          if (product.id != undefined) {
            Promise.all([stockService.warehouseProduct(product.id, this.currentSelectedWarehouse, this.form.batchId)])
            .then((data) => {
              me.form.currentAdjustment = data[0].currentQty;
            });
          }
        }
      }

      if (this.page == 'stock') {
        if (this.currentSelectedWarehouse > 0) {
          if (product.id != undefined) {
            Promise.all([stockService.warehouseProduct(product.id, this.currentSelectedWarehouse, this.form.batchId)])
              .then((data) => {
                if (!isNaN(me.form.physicalCount)) {
                  me.form.variance = (me.form.physicalCount - data[0].qtyNumber).toFixed(2).replace(/[.,]00$/, '');
                  me.form.currentCount = data[0].qtyNumber;
                }
              });
          }
        }
      }
    },

    'form.batchId': function (batchId) {
      if (batchId && this.hasProduction) {
        if (this.page === 'transfer' && this.transferType && this.transferTypeCode === 'TB') {
          return;
        }
        const stockProduct = this.currentStockProducts.find(item => +item.productId.id === +this.form.productId.id);
        let currentProduct = stockProduct.product;
        if (!currentProduct && typeof this.form.productId === 'object') {
          currentProduct = this.form.productId;
        }
        if (currentProduct) {
          let products = currentProduct.warehouses;
          if (this.currentSelectedWarehouse) {
            products = products.filter(p => p.warehouseId.toString() === this.currentSelectedWarehouse.toString());
          }
          const batchProduct = products.find(p => batchId.toString() === p.batchId.toString());
          if (this.page == 'adjustment') {
            this.form.currentAdjustment = batchProduct.qtyNumber;
          }
          if (this.page == 'stock') {
            this.form.currentCount = batchProduct.qtyNumber;
          }
        }
      }
    },

    transferType(transferType) {
      if (this.page === 'transfer' && transferType && this.transferTypeCode === 'TB') {
        this.hiddenFields.push('batchId');
      } else if (this.hiddenFields.indexOf('batchId') !== -1) {
        this.hiddenFields.splice(this.hiddenFields.indexOf('batchId'));
      }

      if (this.isTransferToBatchPage) {
        this.getResources();
      }
    },

    batches(batches) {
      if (batches) {
        const options = batches.map(batch => ({
          id: +batch.id,
          name: batch.code,
        }));
        this.form.setOptions('batchId', [{
          id: 0,
          name: `${this.$t('static.orders.edit.nonBatch')}`,
        }, ...options]);
      }
    },

    'form.physicalCount': function (val) {
      if (!isNaN(val)) {
        this.form.variance = (parseFloat(val) - this.form.currentCount).toFixed(2).replace(/[.,]00$/, '');
      } else {
        this.form.variance = 9;
      }
    },
    currentStockProducts(currentStockProducts) {
      //
    },

    /**
     * Watches state update to inject on Forms class.
     *
     * @param  {Object} products    The products list.
     */
    products(products) {
      // this.form.setOptions('productId', products);
      // this.form.productId = '';
      // store.dispatch('RefreshStockProductsCustom');
    },
  },

  created() {
    Promise.all([organizationSettingsService.show()]).then((data) => {
      this.form.setDisplays('productId', data[0].productInfoDisplay);
    });
  },

  methods: {
    ...mapMutations({
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalProps: 'SET_MODAL_PROPS',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
    }),

    setBatchOptions(selectedProduct) {
      if (selectedProduct && selectedProduct.warehouses) {
        let products = selectedProduct.warehouses;
        if (products.length) {
          if (this.currentSelectedWarehouse) {
            products = products.filter(p => p.warehouseId === this.currentSelectedWarehouse.toString());
          }

          const batchIds = products.map(p => p.batchId.toString());
          
          let options = [];
          options = options.concat(this.batches.filter(b => (batchIds.indexOf(b.id) !== -1)).map((batch) => {
            const batchProduct = products.find(p => p.batchId.toString() === batch.id.toString());
            return Object.assign({}, {
              id: batch.id.toString(),
              name: this.currentSelectedWarehouse ? `${batch.code} | ${this.$t('static.orders.edit.availableQty')}: ${batchProduct.qtyNumber}` : batch.code,
              available: batchProduct.qtyNumber,
            });
          }));
          options = options.reverse();

          const nonBatchProduct = products.find(p => p.batchId === 0);
          if (nonBatchProduct) {
            options.unshift({
              id: '0',
              name: `${this.$t('static.orders.edit.nonBatch')} | ${this.$t('static.orders.edit.availableQty')}: ${nonBatchProduct.qtyNumber}`,
              available: nonBatchProduct.qtyNumber,
            });
          }

          // Counts and adjustments may target an empty batch, so sort only, never hide.
          if (this.currentSelectedWarehouse) {
            options = sortBatchOptions(options);
          }

          this.form.setOverrideOptions('batchId', options);
          if (!this.form.batchId && batchIds.length) {
            this.form.batchId = this.currentSelectedWarehouse ? defaultBatchId(options) : batchIds[0];
          }
        } else {
          this.form.batchId = '';
          this.form.setOverrideOptions('batchId', []);
        }
      } else {
        this.form.batchId = '';
        this.form.setOverrideOptions('batchId', []);
      }
    },

    showBulkAddProductModal() {
      this.setModalContent(() => bulkAddProductModal);
      let warehouseId = 0;
      let warehouseIdForBatch = null;
      if (this.page === 'transfer' && this.transferTypeCode === 'TW') {
        warehouseId =
          this.additionalData && this.additionalData.fromWarehouseId
            ? +this.additionalData.fromWarehouseId
            : '';
      }
      if (this.page === 'purchase') {
        warehouseIdForBatch = +this.additionalData.warehouseId;
      }
      if (['adjustment', 'stock'].includes(this.page)) {
        warehouseIdForBatch = +this.currentSelectedWarehouse;
      }
      this.setModalProps({
        warehouseId,
        fetchProductsMethod: this.fetchAllProductsBasic,
        batches: this.hasProduction && !this.isBatchTransferPage ? this.batches : [],
        onSave: this.handleBulkProductSave,
        warehouseIdForBatch: warehouseIdForBatch || null,
        enableCustomBatch: ['purchase', 'stock', 'adjustment'].includes(this.page),
      });
      this.toggleModal(true);
    },

    fetchAllProductsBasic() {
      let fromWarehouseId = '';
      if (this.page === 'transfer' && this.transferTypeCode === 'TW') {
        fromWarehouseId = this.additionalData && this.additionalData.fromWarehouseId ? this.additionalData.fromWarehouseId : '';
      }

      return Promise.all([
        productService.allBasic({
          filter: 'active',
          warehouse_id: fromWarehouseId,
          include: 'warehouses',
        }),
      ]);
    },

    handleBulkProductSave(selectedProducts) {
      if (this.page === 'stock') {
        if (this.currentSelectedWarehouse > 0) {
          return Promise.all([stockService.warehouseProducts(selectedProducts.map(p => p.id), this.currentSelectedWarehouse)])
            .then((response) => {
              selectedProducts.forEach((p) => {
                const warehouseProduct = this.getWarehouseProduct(response[0], p);
                const data = {
                  id: this.currentStockProducts.length ? this.currentStockProducts[this.currentStockProducts.length - 1].id + 1 : 1,
                  name: p.name,
                  physicalCount: p.qty,
                  variance: warehouseProduct ? (p.qty - warehouseProduct.qtyNumber).toFixed(2).replace(/[.,]00$/, '') : p.qty,
                  currentCount: warehouseProduct ? warehouseProduct.qtyNumber : 0,
                  qty: p.qty,
                  productId: p,
                  product: p,
                  batchId: p.batchId,
                };
                store.dispatch('PushToStockProductsCustom', data);
              });
            });
        }
        return Promise.reject();
      }

      if (this.page === 'adjustment') {
        if (this.currentSelectedWarehouse > 0) {
          return Promise.all([stockService.warehouseProducts(selectedProducts.map(p => p.id), this.currentSelectedWarehouse)])
          .then((response) => {
            selectedProducts.forEach((p) => {
              const warehouseProduct = this.getWarehouseProduct(response[0], p);
              const data = {
                id: this.currentStockProducts.length ? this.currentStockProducts[this.currentStockProducts.length - 1].id + 1 : 1,
                name: p.name,
                qty: p.qty,
                productId: p,
                product: p,
                purchasePrice: 0,
                costPrice: 0,
                currentAdjustment: warehouseProduct ? warehouseProduct.currentQty : 0,
                batchId: p.batchId,
              };
              store.dispatch('PushToStockProductsCustom', data);
            });
          });
        }
        return Promise.reject();
      }

      selectedProducts.forEach((p) => {
        const data = {
          id: this.currentStockProducts.length ? this.currentStockProducts[this.currentStockProducts.length - 1].id + 1 : 1,
          name: p.name,
          qty: p.qty,
          productId: p,
          product: p,
          purchasePrice: 0,
          costPrice: 0,
          batchId: p.batchId,
        };
        store.dispatch('PushToStockProductsCustom', data);
      });
      return Promise.resolve();
    },

    getWarehouseProduct(products, selected) {
      let warehouseProduct = null;
      if (products) {
        if (selected.batchId) {
          warehouseProduct = products.find(wp => +wp.productId === +selected.id && +wp.batchId === +selected.batchId);
        } else {
          warehouseProduct = products.find(wp => +wp.productId === +selected.id);
        }
      }
      return warehouseProduct;
    },

    toFloat(number) {
      return parseFloat(number.replace(/\s/g, '').replace(/,(\d+)$/, '.$1'));
    },

    searchMethod(search, field) {
      return Promise.all([
        productService.search({ search, warehouse_id: '' }),
      ]);
    },
    /**
     * The handler for the quick product edit component that will be passed to the modal.
     *
     * @param  {Object} item The row item.
     * @return {Object}      The component.
     */
    onQuickProductEditMounted() {
      const that = this;
      return function () {
        this.$once('close', () => {
          productService.all().then(() => {
            const filteredProducts = that.products
              .filter(p => p.id.toString() === this.orderProduct.productId.toString());
            if (filteredProducts.length) {
              that.form.assignData(this.orderProduct);
              that.form.assignData(filteredProducts[0]);
              that.update(this.orderProduct.id).then(() => {
                that.form.resetValues();
              });
            }
          });
        });
      };
    },

    /**
     * Defines the handler for the links one the table.
     *
     * @param  {Object} item The row item.
     * @param  {String} name The field name.
     * @return {Mixed}      False or the handler function in case of a link needed.
     */
    tableClickHandler(item, name) {

    },

    /**
     * Saves the form, adding a product.
     */
    save() {
      const data = this.form.data();
      const self = this;
      const product = data.productId;

      if (product) {
        const hasDuplicate = self.currentStockProducts.find(x => x.productId == data.productId.id);
        if (!hasDuplicate) {
          data.id = self.currentStockProducts.length ? self.currentStockProducts[self.currentStockProducts.length - 1].id + 1 : 1;
          data.name = product.name;
          data.product = product;
          store.dispatch('PushToStockProductsCustom', data);
        } else {
          this.$message({
            showClose: true,
            customClass: 'error',
            message: 'Product duplicate found',
          });
          return Promise.reject();
        }
      } else {
        this.$message({
          showClose: true,
          customClass: 'error',
          message: 'Product not found',
        });


        return Promise.reject();
      }

      return Promise.all([]);
    },

    /**
     * Updates a order product.
     */
    update(item, index) {
      const data = this.form.data();
      const self = this;
      const product = data.productId;

      if (product) {
        const currentId = self.currentStockProducts[index].id;
        const hasDuplicate = self.currentStockProducts.find(x => x.productId == product.id && x.id != currentId);

        if (!hasDuplicate) {
          data.id = currentId;
          data.name = product.name;
          data.product = product;
          const i = index;

          store.dispatch('UpdateToStockProducts', { data, i });
        } else {
          this.$message({
            showClose: true,
            customClass: 'error',
            message: 'Product duplicate found',
          });
          return Promise.reject();
        }
      } else {
        this.$message({
          showClose: true,
          customClass: 'error',
          message: 'Product not found',
        });
        return Promise.reject();
      }
      return Promise.all([]);
    },

    /**
     * Delete the product from order.
     */
    destroy(orderProductId) {
      const i = this.currentStockProducts.findIndex(x => x.id == orderProductId);
      store.dispatch('DeleteFromStockProductsCustom', i);
      return Promise.all([]);
    },

    /**
     * Fetch the component's required resources.
     */
    getResources() {
      const resources = [
        organizationSettingsService.show(),
      ];
      if (this.hasProduction) {
        const params = {
          warehouse_id: this.currentSelectedWarehouse || '',
        };
        if (!this.isTransferToBatchPage) {
          params.has_warehouse_transaction = 1;
        }
        resources.push(batchService.all({ ...params, for_select: 1 }));
      }
      Promise.all(resources).then(() => {
        this.ready = true;
      });
    },
  },

  mounted() {
    const fields = this.fields;
    if (!this.hasProduction) {
      delete fields.batchId;
    }
    this.form = new Forms(fields);
    for (const key in this.fields) {
      if (this.fields[key].disabled) {
        this.form.disableField(key);
      }
    }
    if (this.isBatchTransferPage) {
      this.hiddenFields.push('batchId');
    }
    this.getResources();
  },

  components: {
    VFormTable: require('./../formTable/formTable.vue'),
  },
};



// WEBPACK FOOTER //
// ./src/app/components/productTableWithCustomForm/productTableWithCustomForm.js